"use client";

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStores } from "../stores/useStores";
import { useFeatureFlags } from "../utils/featureFlags/useFeatureFlags";
import { getPostHog } from "../utils/posthog";

/**
 * Known analytics event names for type safety.
 * Add new event names here as they are implemented.
 */
export type KnownAnalyticsEvent =
  | "search_submitted"
  | "search_zero_results"
  | "search_result_clicked"
  | "modal_opened"
  | "button_clicked"
  | "contact_form_sent"
  | "subscription_toggled";

/**
 * Event name type that accepts known events with autocomplete
 * but also allows string fallback for flexibility.
 */
export type AnalyticsEventName = KnownAnalyticsEvent | (string & {});

/**
 * User role derived from store permissions.
 */
export type UserRole = "admin" | "editor" | "viewer" | "follower" | "anonymous";

/**
 * Search result types for analytics tracking.
 */
export type ISearchType =
  | "news_items"
  | "event_items"
  | "pages"
  | "staff_members"
  | "google_drive"
  | "files_and_folders";

/**
 * Search context for auto-enriching search-related events.
 */
export interface SearchContext {
  searchTerm: string;
  searchTypes: ISearchType[];
  pages: Partial<Record<ISearchType, number>>;
}

/**
 * Properties automatically enriched on every tracked event.
 */
export interface EnrichedEventProperties {
  organization_id: string | number | null;
  user_id: string | number | null;
  user_role: UserRole;
}

/**
 * Analytics context value providing the track function.
 */
export interface AnalyticsContextValue {
  /**
   * Track an analytics event with automatic enrichment.
   * Events are only sent if the posthog_tracking_enabled feature flag is true.
   *
   * @param eventName - The name of the event to track
   * @param properties - Optional additional properties to include with the event
   */
  track: (
    eventName: AnalyticsEventName,
    properties?: Record<string, unknown>,
  ) => void;

  /**
   * Whether analytics tracking is currently enabled.
   */
  isEnabled: boolean;

  /**
   * Current search context for auto-enrichment of search-related events.
   */
  searchContext: SearchContext | null;

  /**
   * Set the search context for auto-enrichment. Set to null to clear.
   */
  setSearchContext: (ctx: SearchContext | null) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

/**
 * Stable fallback context returned when useAnalyticsSafe is called outside a provider.
 * Defined outside the hook to maintain referential stability.
 */
const fallbackContext: AnalyticsContextValue = {
  track: () => {},
  isEnabled: false,
  searchContext: null,
  setSearchContext: () => {},
};

/**
 * Provider component that enables analytics tracking throughout the application.
 *
 * Features:
 * - Uses the posthog_tracking_enabled feature flag as a master switch
 * - Automatically enriches events with organization_id, user_id, and user_role
 * - Provides type-safe event names with autocomplete for known events
 *
 */
export function AnalyticsProvider({ children }: PropsWithChildren) {
  const { userStore, organizationStore } = useStores();
  const featureFlags = useFeatureFlags();

  const isEnabled = featureFlags.posthog_tracking_enabled;

  const orgId = organizationStore.currentOrganization?.id ?? null;
  const userId = userStore.id;
  const isAdmin = userStore.admin;
  const isEditor = userStore.editor;
  const isViewer = userStore.viewer;
  const isFollower = userStore.follower;

  const [searchContext, setSearchContext] = useState<SearchContext | null>(
    null,
  );

  // Track if user has been identified this session
  const hasIdentifiedRef = useRef(false);

  // Tag events with org regardless of the analytics flag, so captured
  // errors still carry org context.
  useEffect(() => {
    if (orgId === null) return;
    getPostHog().group("organization", String(orgId));
  }, [orgId]);

  // Identify already-logged-in users on mount
  useEffect(() => {
    if (!isEnabled || hasIdentifiedRef.current || userId === null) {
      return;
    }

    try {
      const posthog = getPostHog();
      posthog.identify(String(userId), {
        username: userStore.username,
        first_name: userStore.firstName,
        last_name: userStore.lastName,
        organization_id: orgId,
      });
      hasIdentifiedRef.current = true;
    } catch (error) {
      // Silently fail if PostHog is not available
      if (process.env.NEXT_PUBLIC_APP_ENV === "dev") {
        console.warn("[Analytics] Failed to identify user:", error);
      }
    }
  }, [
    isEnabled,
    userId,
    userStore.username,
    userStore.firstName,
    userStore.lastName,
    orgId,
  ]);

  const track = useCallback(
    (eventName: AnalyticsEventName, properties?: Record<string, unknown>) => {
      if (!isEnabled) {
        return;
      }

      let userRole: UserRole = "anonymous";
      if (isAdmin) userRole = "admin";
      else if (isEditor) userRole = "editor";
      else if (isViewer) userRole = "viewer";
      else if (isFollower) userRole = "follower";

      const enrichedProperties: EnrichedEventProperties &
        Record<string, unknown> = {
        organization_id: orgId,
        user_id: userId,
        user_role: userRole,
        ...properties,
      };

      // Auto-enrich with search context if present
      if (searchContext) {
        enrichedProperties.search_term = searchContext.searchTerm;
        enrichedProperties.search_types_enabled = searchContext.searchTypes;
        // Include page for the result type if provided in properties
        if (
          properties?.result_type &&
          typeof properties.result_type === "string"
        ) {
          const resultType = properties.result_type as ISearchType;
          enrichedProperties.search_page = searchContext.pages[resultType] ?? 1;
        }
      }

      try {
        const posthog = getPostHog();
        posthog.capture(eventName, enrichedProperties);
      } catch (error) {
        // Silently fail if PostHog is not available
        // This can happen during SSR or if PostHog fails to load
        if (process.env.NEXT_PUBLIC_APP_ENV === "dev") {
          console.warn("[Analytics] Failed to track event:", eventName, error);
        }
      }
    },
    [
      isEnabled,
      orgId,
      userId,
      isAdmin,
      isEditor,
      isViewer,
      isFollower,
      searchContext,
    ],
  );

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      track,
      isEnabled,
      searchContext,
      setSearchContext,
    }),
    [track, isEnabled, searchContext],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * Hook to safely access analytics without throwing if provider is missing.
 * Returns a no-op track function if not within provider.
 *
 * @example
 * ```tsx
 * function OptionalTrackingComponent() {
 *   const { track, isEnabled } = useAnalyticsSafe();
 *   // Works even without AnalyticsProvider
 *   track('button_clicked', { button_name: 'submit' });
 * }
 * ```
 */
export function useAnalyticsSafe(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);
  return context ?? fallbackContext;
}
